import { Injectable } from '@angular/core';
import { MapsAPILoader } from 'ng-agm-core-lib';

/**
 * Service to render Google Maps Directions
 * Replaces the incompatible agm-direction library
 */
@Injectable()
export class DirectionRendererService {
    private directionsService: google.maps.DirectionsService;
    private directionsRenderer: google.maps.DirectionsRenderer;
    private mapInstance: google.maps.Map;

    constructor(private mapsAPILoader: MapsAPILoader) { }

    /**
     * Initialize the direction service with map instance
     */
    initialize(map: google.maps.Map): void {
        if (!map) {
            console.error('Cannot initialize DirectionRenderer: map instance is null');
            return;
        }

        this.mapInstance = map;

        try {
            this.directionsService = new google.maps.DirectionsService();
            this.directionsRenderer = new google.maps.DirectionsRenderer({
                suppressMarkers: false,
                preserveViewport: false
            });
            this.directionsRenderer.setMap(this.mapInstance);
        } catch (error) {
            console.error('Failed to initialize DirectionRenderer:', error);
        }
    }

    /**
     * Render a route on the map
     * @param origin Starting point (address string or LatLng)
     * @param destination End point (address string or LatLng)
     * @param waypoints Array of intermediate stops
     * @param travelMode Travel mode (DRIVING, WALKING, BICYCLING, TRANSIT)
     * @returns Promise with DirectionsResult or error
     */
    renderRoute(
        origin: string | google.maps.LatLng | google.maps.LatLngLiteral,
        destination: string | google.maps.LatLng | google.maps.LatLngLiteral,
        waypoints: any[] = [],
        travelMode: string = 'DRIVING'
    ): Promise<google.maps.DirectionsResult> {
        if (!this.directionsService || !this.directionsRenderer) {
            return Promise.reject('Direction service not initialized. Call initialize() first.');
        }

        const request: google.maps.DirectionsRequest = {
            origin: origin,
            destination: destination,
            waypoints: waypoints.map(wp => ({
                location: wp.location || wp,
                stopover: wp.stopover !== undefined ? wp.stopover : true
            })),
            travelMode: this._getTravelMode(travelMode),
            optimizeWaypoints: false
        };

        return new Promise((resolve, reject) => {
            this.directionsService.route(request, (result, status) => {
                if (status === google.maps.DirectionsStatus.OK) {
                    this.directionsRenderer.setDirections(result);
                    resolve(result);
                } else if (status === google.maps.DirectionsStatus.ZERO_RESULTS) {
                    this.clearRoute();
                    reject({ status: 'ZERO_RESULTS', message: 'No route found' });
                } else {
                    this.clearRoute();
                    reject({ status, message: `Directions request failed: ${status}` });
                }
            });
        });
    }

    /**
     * Clear the current route from the map
     */
    clearRoute(): void {
        if (this.directionsRenderer) {
            this.directionsRenderer.setDirections({ routes: [], geocoded_waypoints: [] } as any);
        }
    }

    /**
     * Update renderer options
     */
    setOptions(options: google.maps.DirectionsRendererOptions): void {
        if (this.directionsRenderer) {
            this.directionsRenderer.setOptions(options);
        }
    }

    /**
     * Get DirectionsRenderer instance for advanced usage
     */
    getRenderer(): google.maps.DirectionsRenderer {
        return this.directionsRenderer;
    }

    /**
     * Convert string travel mode to google.maps.TravelMode
     */
    private _getTravelMode(mode: string): google.maps.TravelMode {
        const modeUpper = mode.toUpperCase();
        switch (modeUpper) {
            case 'DRIVING':
                return google.maps.TravelMode.DRIVING;
            case 'WALKING':
                return google.maps.TravelMode.WALKING;
            case 'BICYCLING':
                return google.maps.TravelMode.BICYCLING;
            case 'TRANSIT':
                return google.maps.TravelMode.TRANSIT;
            default:
                return google.maps.TravelMode.DRIVING;
        }
    }

    /**
     * Cleanup when component is destroyed
     */
    destroy(): void {
        this.clearRoute();
        if (this.directionsRenderer) {
            this.directionsRenderer.setMap(null);
        }
    }
}
